import { FlexibleModuleEnum } from 'generated/graphql';
import type { PageBuilderModuleType } from '~/modules/page-builder/types';
import SingleImageModule from '~/modules/page-builder/component/detail/single-image-module/SingleImageModule';
import DuoParagraphModule from '../duo-paragraph-module/DuoParagraphModule';
import DuoImageModule from '../duo-image-module/DuoImageModule';
import TrioImageModule from '../trio-image-module/TrioImageModule';
import SingleParagraphModule from '../single-paragraph-module/SingleParagraphModule';
import ImageParagraphModule from '../image-paragraph-module/ImageParagraphModule';
import GalleryModule from '../gallery-module/GalleryModule';
import SingleVideoModule from '../single-video-module/SingleVideoModule';
import VideoParagraphModule from '../video-paragraph-module/VideoParagraphModule';
import QuoteModule from '../quote-module/QuoteModule';

import HtmlModuleDisplay from '@modules/page-builder/component/detail/html-module/HtmlModuleDisplay';
import FullPageGalleryModule from '../full-page-gallery-module/FullPageGalleryModule';
import { useAtomValue } from 'jotai';
import { currentCategoryAtom } from '@rocco/ui/website-header/states/current-category-atom';
import FullPageImageModule from '../single-image-module/FullPageImageModule';
interface DetailModuleProps {
    module: PageBuilderModuleType;
    autoPlayVideoModuleId?: string;
}

/**
 * A rendered Page Builder module on the website. The module component rendered
 * depends on the module type.
 */
const DetailModule = ({ module, autoPlayVideoModuleId }: DetailModuleProps) => {
    const columns = module.columns;
    const meta = module.meta;
    const currentCategory = useAtomValue(currentCategoryAtom);
    const firstColumn = columns[0];
    if (!firstColumn) {
        // A failed-to-map module arrives here with empty columns. Render the
        // notice before bailing, otherwise the section silently vanishes.
        if (module.presetType === FlexibleModuleEnum.ErrorLoadingModule) {
            return (
                <p className="text-charcoal-250 mx-auto w-full max-w-[56.25rem] px-4.5">
                    This section could not be loaded.
                </p>
            );
        }
        return null;
    }

    const isProject = currentCategory === 'Projects';

    switch (module.presetType) {
        case FlexibleModuleEnum.ImageParagraphModule:
            return <ImageParagraphModule columns={columns} meta={meta} />;
        case FlexibleModuleEnum.VideoParagraphModule:
            return <VideoParagraphModule columns={columns} meta={meta} />;
        case FlexibleModuleEnum.SingleVideoModule:
            return (
                <SingleVideoModule
                    column={firstColumn}
                    shouldAutoPlayVideo={autoPlayVideoModuleId === module.id}
                    isCoverGrayscale={module.isCoverGrayscale}
                />
            );
        case FlexibleModuleEnum.HeadingModule:
        case FlexibleModuleEnum.SingleParagraphModule:
            return <SingleParagraphModule column={firstColumn} meta={meta} />;
        case FlexibleModuleEnum.DuoParagraphModule:
            return <DuoParagraphModule columns={columns} meta={meta} />;
        case FlexibleModuleEnum.QuoteModule:
            return <QuoteModule column={firstColumn} meta={meta} />;
        case FlexibleModuleEnum.SingleImageModule:
            return (
                <SingleImageModule column={firstColumn} isProject={isProject} />
            );
        case FlexibleModuleEnum.FullPageImageModule:
            return <FullPageImageModule column={firstColumn} />;
        case FlexibleModuleEnum.DuoImageModule:
            return <DuoImageModule columns={columns} />;
        case FlexibleModuleEnum.TrioImageModule:
            return <TrioImageModule columns={columns} />;
        case FlexibleModuleEnum.QuadImageModule:
        case FlexibleModuleEnum.OctaImageModule:
            return <GalleryModule columns={columns} />;
        case FlexibleModuleEnum.HtmlModule:
            return <HtmlModuleDisplay column={firstColumn} meta={meta} />;
        case FlexibleModuleEnum.FullPageImagesSliderModule:
            return <FullPageGalleryModule columns={columns} />;
        default:
            return null;
    }
};

export default DetailModule;
